import React, { useState, useRef, createElement } from 'react';
import { Invoice } from '../../types';
import { Button } from '../../components/ui/Button';
import { Printer, Download, X } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { formatCurrency } from './utils';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
interface Props {
  invoice: Invoice;
  onClose: () => void;
}
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const InvoiceModal: React.FC<Props> = ({
  invoice,
  onClose
}) => {
  const {
    patients,
    walkInClients
  } = useData();
  const printableRef = useRef<HTMLDivElement>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const getCustomer = () => {
    if (invoice.customerType === 'Patient') {
      return patients.find(p => p.id === invoice.customerId);
    } else {
      return walkInClients.find(c => c.id === invoice.customerId);
    }
  };
  const customer = getCustomer();
  const handlePrint = () => {
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_${invoice.invoiceNumber}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-1141">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-1142">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-1143">
          <h3 className="text-lg font-semibold text-gray-900" data-id="element-1144">
            Invoice {invoice.invoiceNumber}
          </h3>
          <div className="flex items-center space-x-2" data-id="element-1145">
            <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-1146">
              <Printer className="w-4 h-4 mr-1" data-id="element-1147" /> Print
            </Button>
            <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-1148">
              <Download className="w-4 h-4 mr-1" data-id="element-1149" />
              {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
            </Button>
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-1150">
              <X className="w-5 h-5 text-gray-500" data-id="element-1151" />
            </button>
          </div>
        </div>

        <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-1152">
          <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-1153">
            <div className="flex items-center" data-id="element-1154">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-1155" />
              <div data-id="element-1156">
                <h1 className="text-2xl font-bold uppercase text-pink-600" data-id="element-1157">
                  BMED Medical Centre
                </h1>
                <p className="text-xs italic text-gray-600" data-id="element-1158">
                  Maternity Care Made Better
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-1159">
              <h2 className="text-3xl font-bold uppercase tracking-widest mb-2 text-gray-800" data-id="element-1160">
                INVOICE
              </h2>
              <p data-id="element-1161">
                <span className="font-semibold" data-id="element-1162">Invoice #:</span>{' '}
                {invoice.invoiceNumber}
              </p>
              <p data-id="element-1163">
                <span className="font-semibold" data-id="element-1164">Date:</span>{' '}
                {new Date(invoice.date).toLocaleDateString()}
              </p>
              <p data-id="element-1165">
                <span className="font-semibold" data-id="element-1166">Due Date:</span>{' '}
                {new Date(invoice.dueDate).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="mb-8 flex justify-between" data-id="element-1167">
            <div data-id="element-1168">
              <h3 className="font-bold uppercase text-gray-600 mb-2" data-id="element-1169">
                Bill To:
              </h3>
              {invoice.customerType === 'Patient' ? <>
                  <p className="font-bold text-lg" data-id="element-1170">
                    {(customer as any)?.firstName} {(customer as any)?.lastName}
                  </p>
                  <p data-id="element-1171">File No: {(customer as any)?.bmcFileNumber}</p>
                  <p data-id="element-1172">{(customer as any)?.address}</p>
                  <p data-id="element-1173">{(customer as any)?.contact}</p>
                </> : <>
                  <p className="font-bold text-lg" data-id="element-1174">
                    {invoice.customerName || (customer as any)?.name || 'Walk-in client'}
                  </p>
                  {customer && <>
                      <p data-id="element-1175">{(customer as any)?.address}</p>
                      <p data-id="element-1176">{(customer as any)?.phone}</p>
                      <p data-id="element-1177">{(customer as any)?.email}</p>
                    </>}
                </>}
            </div>
            <div className="text-right" data-id="element-1178">
              <h3 className="font-bold uppercase text-gray-600 mb-2" data-id="element-1179">
                Payment Status:
              </h3>
              <p className="text-xl font-bold uppercase" data-id="element-1180">{invoice.status}</p>
              <p className="mt-2" data-id="element-1181">
                <span className="font-semibold" data-id="element-1182">Amount Paid:</span>{' '}
                {formatCurrency(invoice.amountPaid)}
              </p>
              <p className="text-red-600" data-id="element-1183">
                <span className="font-semibold" data-id="element-1184">Balance Due:</span>{' '}
                {formatCurrency(invoice.balanceDue)}
              </p>
            </div>
          </div>

          <table className="w-full border-collapse mb-8" data-id="element-1185">
            <thead data-id="element-1186">
              <tr className="bg-gray-100 border-b-2 border-gray-300" data-id="element-1187">
                <th className="p-2 text-left font-bold uppercase text-gray-600" data-id="element-1188">
                  Item
                </th>
                <th className="p-2 text-right font-bold uppercase text-gray-600" data-id="element-1189">
                  Qty
                </th>
                <th className="p-2 text-right font-bold uppercase text-gray-600" data-id="element-1190">
                  Rate
                </th>
                <th className="p-2 text-right font-bold uppercase text-gray-600" data-id="element-1191">
                  Disc(%)
                </th>
                <th className="p-2 text-right font-bold uppercase text-gray-600" data-id="element-1192">
                  Tax(%)
                </th>
                <th className="p-2 text-right font-bold uppercase text-gray-600" data-id="element-1193">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1194">
              {invoice.items.map((item, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-1195">
                  <td className="p-2" data-id="element-1196">
                    <p className="font-semibold" data-id="element-1197">{item.serviceName}</p>
                    {item.description && <p className="text-xs text-gray-500" data-id="element-1198">
                        {item.description}
                      </p>}
                  </td>
                  <td className="p-2 text-right" data-id="element-1199">{item.quantity}</td>
                  <td className="p-2 text-right" data-id="element-1200">
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td className="p-2 text-right" data-id="element-1201">{item.discount}%</td>
                  <td className="p-2 text-right" data-id="element-1202">{item.tax}%</td>
                  <td className="p-2 text-right font-medium" data-id="element-1203">
                    {formatCurrency(item.total)}
                  </td>
                </tr>)}
            </tbody>
          </table>

          <div className="flex justify-between" data-id="element-1204">
            <div className="w-1/2 pr-8" data-id="element-1205">
              {invoice.notes && <>
                  <h4 className="font-bold uppercase text-gray-600 mb-2" data-id="element-1206">
                    Notes
                  </h4>
                  <p className="text-gray-700 whitespace-pre-wrap" data-id="element-1207">
                    {invoice.notes}
                  </p>
                </>}
              {invoice.paymentTerms && <div className="mt-4" data-id="element-1208">
                  <h4 className="font-bold uppercase text-gray-600 mb-2" data-id="element-1209">
                    Terms
                  </h4>
                  <p className="text-gray-700" data-id="element-1210">{invoice.paymentTerms}</p>
                </div>}
            </div>
            <div className="w-1/2 max-w-xs" data-id="element-1211">
              <div className="flex justify-between py-1" data-id="element-1212">
                <span className="text-gray-600" data-id="element-1213">Subtotal:</span>
                <span className="font-medium" data-id="element-1214">
                  {formatCurrency(invoice.subtotal)}
                </span>
              </div>
              <div className="flex justify-between py-1" data-id="element-1215">
                <span className="text-gray-600" data-id="element-1216">Discount:</span>
                <span className="font-medium text-red-600" data-id="element-1217">
                  -{formatCurrency(invoice.totalDiscount)}
                </span>
              </div>
              <div className="flex justify-between py-1 border-b border-gray-300" data-id="element-1218">
                <span className="text-gray-600" data-id="element-1219">Tax:</span>
                <span className="font-medium" data-id="element-1220">
                  {formatCurrency(invoice.totalTax)}
                </span>
              </div>
              <div className="flex justify-between py-3 text-lg font-bold" data-id="element-1221">
                <span data-id="element-1222">Total:</span>
                <span className="text-blue-700" data-id="element-1223">
                  {formatCurrency(invoice.totalAmount)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};